import type { Schemas } from '@argzombie/api-client';
import { RECIPE_WHERE } from '../../lib/s2/types';
import { RequirementsEditor } from './s2/TechForms';
import {
  Fieldset,
  IssueList,
  NumberField,
  SelectField,
  StackListEditor,
  TextField,
} from './fields';
import { useContentRefs } from './refs';
import type { FormProps } from './types';

/** ADR 0066 reserves `requiresSkill` (validated, nothing gates in v1). */
type Recipe = Schemas['RecipeDef'] & { requiresSkill?: { id?: string; level?: number } | null };

export function RecipeForm({ value: r, onChange, errors }: FormProps<Recipe>) {
  const set = <K extends keyof Recipe>(k: K, v: Recipe[K]) => onChange({ ...r, [k]: v });
  const { baseModules } = useContentRefs();
  const module = baseModules.find((m) => m.id === r.requiredModuleId);
  const levels = module?.levels?.length ?? 0;

  return (
    <>
      <Fieldset legend="Recipe">
        <TextField label="Id" mono value={r.id} issues={errors.id} onChange={(v) => set('id', v)} />
        <TextField
          label="Name"
          value={r.name}
          issues={errors.name}
          onChange={(v) => set('name', v)}
        />
        <SelectField
          label="Required module"
          hint="Legacy (read for one release)."
          none="(none)"
          value={r.requiredModuleId}
          options={baseModules.map((m) => ({
            value: m.id ?? '',
            label: `${m.name || m.id} (${m.id})`,
          }))}
          issues={errors.requiredModuleId}
          onChange={(v) =>
            onChange({
              ...r,
              requiredModuleId: v,
              requiredModuleLevel: v ? Math.max(1, r.requiredModuleLevel ?? 0) : 0,
            })
          }
        />
        <NumberField
          label="Module level"
          hint={r.requiredModuleId ? `Minimum level, 1..${levels}.` : 'Ignored without a module.'}
          integer
          min={r.requiredModuleId ? 1 : 0}
          max={levels || undefined}
          value={r.requiredModuleLevel}
          issues={errors.requiredModuleLevel}
          onChange={(v) => set('requiredModuleLevel', v ?? 0)}
        />
      </Fieldset>
      <Fieldset legend="Skill requirement (later)">
        <p className="text-xs text-bruise sm:col-span-2">
          Reserved for a later release (ADR 0066): nothing gates on it yet. Current:{' '}
          <span className="font-mono">
            {r.requiresSkill ? `${r.requiresSkill.id ?? ''} ${r.requiresSkill.level ?? 0}` : 'none'}
          </span>
          . Edit in JSON.
        </p>
        <IssueList issues={errors.requiresSkill} />
      </Fieldset>
      <Fieldset legend="Tech">
        <NumberField
          label="Tier"
          integer
          min={0}
          value={r.tier ?? 0}
          issues={errors.tier}
          onChange={(v) => set('tier', v ?? 0)}
        />
        <SelectField
          label="Where"
          hint="Home = the browser workshop; field = the phone's field kit."
          value={r.where || 'home'}
          options={RECIPE_WHERE.map((w) => ({ value: w, label: w }))}
          issues={errors.where}
          onChange={(v) => set('where', v === 'home' ? '' : (v ?? ''))}
        />
        <NumberField
          label="Power (Wh)"
          integer
          min={0}
          value={r.powerWh ?? 0}
          issues={errors.powerWh}
          onChange={(v) => set('powerWh', v ?? 0)}
        />
        <NumberField
          label="Craft seconds"
          hint="0 = the tier default."
          integer
          min={0}
          value={r.craftSeconds ?? 0}
          issues={errors.craftSeconds}
          onChange={(v) => set('craftSeconds', v ?? 0)}
        />
        <RequirementsEditor
          value={r.requires}
          base="requires"
          errors={errors}
          onChange={(v) => set('requires', v)}
        />
      </Fieldset>
      <Fieldset legend="Inputs and outputs">
        <StackListEditor
          label="Inputs"
          hint="Taken from base storage."
          base="inputs"
          value={r.inputs}
          errors={errors}
          onChange={(v) => set('inputs', v)}
        />
        <StackListEditor
          label="Outputs"
          hint="Added to base storage."
          base="outputs"
          value={r.outputs}
          errors={errors}
          onChange={(v) => set('outputs', v)}
        />
      </Fieldset>
    </>
  );
}
